<template>
    <div class="exam-result-management">
        <div class="patient-register-container">
            <div class="action-bar">
                <div class="btn-group mb20">
                    <button class="load-btn"   @click="openModal('uploadPatientModal')">불러오기</button>
                    <button class="delete-btn" @click="deleteSelected">삭제</button>
                    <button class="save-btn"   @click="saveAll">저장</button>
                </div>
            </div>
            <div class="table-wrap">
                <table class="patient-table">
                    <colgroup>
                        <col style="width: 8%" />
                        <col style="width: 20%" />
                        <col style="width: auto" />
                        <col style="width: 18%" />
                        <col style="width: 13%" />
                        <col style="width: 20%" />
                        <col style="width: 8%" />
                    </colgroup>
                <thead>
                    <tr>
                        <th><input type="checkbox" v-model="selectAll" @change="toggleSelectAll" /></th>
                        <th>환자번호</th>
                        <th>환자명</th>
                        <th>생년월일</th>
                        <th>몸무게(kg)</th>
                        <th>전화번호</th>
                        <th>수정</th>
                    </tr>
                </thead>
                <tbody>
                    <!-- 기존 환자 목록 -->
                    <tr v-for="(patient, idx) in mappedList" :key="patient.id">
                        <td>
                            <input type="checkbox" v-model="selected" :value="patient.id" />
                        </td>
                        <td>{{ patient.id }}</td>
                        <td>
                            <span v-if="editIdx !== idx">{{ patient.name }}</span>
                            <input
                                v-else
                                v-model     = "editPatient.name"
                                maxlength   = "50"
                                class       = "cell-input"
                                placeholder ="환자명"
                                @input="editPatient.name = filterName(editPatient.name)"
                            />
                        </td>
                        <td>
                            <span v-if="editIdx !== idx">{{ patient.birth }}</span>
                            <VueDatePicker
                                v-else
                                v-model             = "editPatient.birth"
                                format              = "yyyy-MM-dd"
                                input-class         = "cell-input"
                                :enable-time-picker = "false"
                                :model-type         = "'format'"
                                :max-date           = "maxBirthDate"
                                placeholder         = "생년월일"
                                hide-input-icon
                                :clearable="false"
                                teleport="body"
                            />
                        </td>
                        <td>
                            <span v-if="editIdx !== idx">{{ convertUtils.convertWeight(patient.weight) }}</span>
                            <input
                                v-else
                                v-model = "editPatient.weight"
                                type    = "number"
                                max     = "500"
                                class   = "cell-input"
                                placeholder="몸무게"
                            />
                        </td>
                        <td>
                            <span v-if="editIdx !== idx">{{ formatPhone(patient.phone) }}</span>
                            <input
                                v-else
                                v-model     = "editPatient.phone"
                                maxlength   = "20"
                                class       = "cell-input"
                                placeholder = "전화번호"
                                @input="editPatient.phone = filterPhone(editPatient.phone)"
                            />
                        </td>
                        <td>
                            <button v-if="editIdx !== idx" @click="startEdit(idx, patient)">
                                <img src="@/assets/resource/icon/edit.png" alt="수정" style="width: 20px;" />
                            </button>
                            <button v-else class="table-add-btn" @click="saveEdit(idx)">저장</button>
                        </td>
                    </tr>
                    <!-- 새 환자 등록 입력 행 -->
                    <tr v-for="(input, idx) in inputRows" :key="input._tempId">
                        <td>
                            <button class="table-del-btn" @click="delInputRow(idx)">삭제</button>
                        </td>
                        <td></td>
                        <td>
                            <input 
                                v-model="input.name" 
                                placeholder="환자명" 
                                class="cell-input" 
                                maxlength="50"
                                @input="input.name = filterName(input.name)"    
                            />
                        </td>
                        <td>
                            <VueDatePicker
                                v-model             = "input.dob"
                                placeholder         = "생년월일"
                                input-class         = "cell-input"
                                :enable-time-picker = "false"
                                format              = "yyyy-MM-dd"
                                :model-type         = "'format'"
                                :max-date           = "maxBirthDate"
                                hide-input-icon
                                :clearable="false"
                            />
                        </td>
                        <td>
                            <input v-model="input.weight" type="number" placeholder="몸무게" class="cell-input" />
                        </td>
                        <td>
                            <input 
                                v-model="input.contactNumber" 
                                placeholder="전화번호" 
                                class="cell-input" 
                                maxlength="11"
                                @input="input.contactNumber = filterPhone(input.contactNumber)"
                            />
                        </td>
                        <td>
                            <button class="table-add-btn" @click="addInputRow">추가</button>
                        </td>
                        </tr>
                    </tbody>
                </table>
            </div>
            <pagination
                :total-items = "totalCount"
                :page-size   = "pageSize"
                v-model      = "currentPage"
                @page-change = "onPageChange"
            />
        </div>
    </div>
</template>
<script setup>
    defineOptions({
        meta: { title: "환자 등록" },
    });

    import {ref, onMounted, computed, inject} from 'vue'
    import { useRouter }                      from 'vue-router'
    import api, {urls}                        from "@/api";
    import Pagination                         from "@/components/common/Pagination.vue";
    import { useModal }                       from '@/composables/useModal'
    import { useAuthStore }                   from '@/stores/auth';
    import VueDatePicker                      from '@vuepic/vue-datepicker';
    import '@vuepic/vue-datepicker/dist/main.css';
    import { convertUtils }                   from '@/utils/convertUtils'
    import { useAppAlert }                    from '@/composables/useAppAlert'

    const { openModal, onModalEvent } = useModal()
    const { showAlert, showAlertAndMove, confirm } = useAppAlert()

    const authStore = useAuthStore();
    const inst_cd   = authStore.getUser?.inst_cd;

    const router    = useRouter()
    const patients  = ref([])
    const inputRows = ref([
        { id: '', name: '', dob: '', weight: '', contactNumber: '', _tempId: Date.now(), instCd: inst_cd }
    ]);

    const mappedList    = ref([])
    const currentPage   = ref(1)
    const pageSize      = ref(10)
    const totalPages    = ref(1)
    const totalCount    = ref(0)
    const selected      = ref([])         // 체크박스 선택된 id 배열
    const selectAll     = ref(false)      // 전체 선택 여부
    const editIdx       = ref(null)       // 수정 모드
    const editPatient   = ref({})

    const maxBirthDate = new Date();
    maxBirthDate.setHours(0, 0, 0, 0);
    maxBirthDate.setDate(maxBirthDate.getDate() - 1);

    const getData = (data) => {
        if (data) openModal("loadPatientModal");
    };
    const getSaveData = (data) => {
        if (data) fetchPatients();
    };

    function onPageChange(page) {
        fetchPatients(page);
    }

    function fetchPatients(page = 1) {
        api.get(urls.patientAdd.patientList, {
            params: {
                startIndex: page,
                pageRow   : pageSize.value,
                instCd    : inst_cd,
            },
        }).then((res) => {
            const rawList = res.data.list || res.data || [];
            mappedList.value = rawList.map((item) => ({
              id        : item.patient_id,
              name      : item.name || "",
              birth     : item.dob || "",
              weight    : item.weight || "",
              phone     : item.contact_number || "",
              startIndex: page,
              pageRow   : pageSize.value,
            }));

            patients.value    = mappedList.value;
            totalCount.value  = res.data.totCnt || 0;
            totalPages.value  = Math.ceil(totalCount.value / pageSize.value);
            currentPage.value = page;
        
        }).catch(() => {
            showAlert("환자 목록을 불러오는데 실패했습니다.");
            mappedList.value  = [];
            patients.value    = [];
            totalCount.value  = 0;
            totalPages.value  = 1;
            currentPage.value = 1;
          });
    }

    function toggleSelectAll() {
        if (selectAll.value) {
            selected.value = patients.value.map((p) => p.id);
        } else {
            selected.value = [];
        }
    }

    function addInputRow() {
        inputRows.value.push({
            id            : "",
            name          : "",
            dob           : "",
            weight        : "",
            contactNumber : "",
            _tempId: Date.now() + Math.random(),
            instCd: inst_cd,
        });
    }

    function delInputRow(idx) {
        inputRows.value.splice(idx, 1);
        if (inputRows.value.length === 0) addInputRow();
    }

    function startEdit(idx, patient) {
        editIdx.value = idx;
        editPatient.value = { ...patient };
    }

    async function saveEdit(idx) {
        const ok = await confirm("정말로 저장하시겠습니까?");
        if (!ok) { 
            return;
        }

      const updatedPatient = { ...editPatient.value, inst_cd: inst_cd };

      api.post(urls.patientAdd.patientModify, updatedPatient)
         .then((res) => {
              patients.value[idx] = updatedPatient;

              if (res.data === 1) {
                  patients.value[idx] = updatedPatient;
                  editIdx.value = null;
                  editPatient.value = {};
                  showAlert("환자 정보가 성공적으로 저장되었습니다!");
              } else {
                  showAlert("환자 정보 저장에 실패했습니다. (코드: " + res.data + ")");
              }
      }).catch(() => {
          showAlert("환자 정보 저장에 실패했습니다.");
      });
    }

    async function deleteSelected() {
        const ok = await confirm("정말로 삭제하시겠습니까?", { okText: "삭제", cancelText: "취소" });
        if (!ok) { 
            return;
        }

        api.post(urls.patientAdd.patientDelete, { idList: selected.value })
           .then((res) => {
              if (res.data === 1) {
                  patients.value = patients.value.filter((p) => !selected.value.includes(p.id));
                  selected.value = [];
                  selectAll.value = false;
                  fetchPatients(currentPage.value);
                  showAlert("환자 정보 삭제에 성공했습니다!");
              } else {
                  showAlert("환자 정보 삭제에 실패했습니다. (코드: " + res.data + ")");
              }
        }).catch(() => {
            showAlert("환자 정보 삭제에 실패했습니다.");
        });
    }

    async function saveAll() {
        if (inputRows.value.length === 0) {
            showAlert("저장할 신규 환자가 없습니다.");
            return;
        }

        for (const input of inputRows.value) {
            if (!input.name || input.name.trim() === "") {
                showAlert("환자명을 입력해주세요.");
                return;
            }
            if (!/^[a-zA-Z0-9가-힣]+$/.test(input.name)) {
                showAlert("환자명에는 특수문자를 사용할 수 없습니다.");
                return;
            }
            if (!input.dob || input.dob.trim() === "") {
                showAlert("생년월일을 입력해주세요.");
                return;
            }
            if (!/^[0-9]{9,11}$/.test(input.contactNumber)) {
                showAlert("전화번호는 숫자만 입력 가능합니다.");
                return;
            }
        }

        const ok = await confirm("정말로 저장하시겠습니까?", { okText: "저장", cancelText: "취소" });
        if (!ok) {
            return;
        }
        api.post(urls.patientAdd.patientRegister, inputRows.value)
           .then((res) => {
                if (res.data >= 1) {
                    showAlert("신규 환자들이 저장되었습니다.");
                    inputRows.value = [];
                    addInputRow();
                    fetchPatients(currentPage.value);
                } else {
                    showAlert(`저장 실패 (코드: ${res.data})`);
                }
          }).catch(() => {
              showAlert("저장 중 오류가 발생했습니다.");
          });
    }

    function goBack() {
        router.back();
    }

    function formatPhone(phone) {
        if (!phone) return "";
            const onlyNums = phone.replace(/[^0-9]/g, "");
        if (onlyNums.length === 11) {
            return onlyNums.replace(/(\d{3})(\d{4})(\d{4})/, "$1-$2-$3");
        } else if (onlyNums.length === 10) {
            return onlyNums.replace(/(\d{3})(\d{3,4})(\d{4})/, "$1-$2-$3");
        }
        return phone;
    }

    function filterPhone(value) {
        if (!value) return "";

        return value.replace(/[^0-9]/g, "");
    }


    function filterName(value) {
        if (!value) return "";

        // 한글, 영어, 숫자만 허용
        return value.replace(/[^a-zA-Z0-9가-힣]/g, "")
    }

    onMounted(() => {
        fetchPatients();
        onModalEvent("uploadPatientModal", "onSubmit", getData);
        onModalEvent("loadPatientModal", "onSubmit", getSaveData);
    });
</script>
<style scoped>
    /* ✅ 테이블 셀 정렬 */
    .patient-table td {
        vertical-align: middle;
    }

    /* ✅ 공통 입력 디자인 (수정모드 + 신규등록 공통) */
    .patient-table .cell-input {
    width: 100%;
    height: 34px;                 /* 깔끔한 높이 */
    padding: 0 12px;
    border: 1px solid #d7dde5;
    border-radius: 8px;
    background: #fff;
    font-size: 14px;
    color: #111;
    box-sizing: border-box;
    transition: border-color 0.15s, box-shadow 0.15s;
    }

    .patient-table .cell-input::placeholder {
    color: #9aa6b2;
    opacity: 1;
    }

    .patient-table .cell-input:focus {
    outline: none;
    border-color: #5b9bff;
    box-shadow: 0 0 0 3px rgba(91, 155, 255, 0.15);
    }

    /* ✅ number input 화살표 제거 (선택) */
    .patient-table input[type="number"]::-webkit-outer-spin-button,
    .patient-table input[type="number"]::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
    }
    .patient-table input[type="number"] {
    -moz-appearance: textfield;
    }

    /* ✅ VueDatePicker wrapper가 td 폭을 그대로 따라가게 */
    :deep(.dp__main),
    :deep(.dp__input_wrap) {
    width: 100% !important;
    max-width: 100% !important;
    box-sizing: border-box;
    border: none !important;
    box-shadow: none !important;
    }

    /* ✅ VueDatePicker 내부 input도 완전 동일하게 */
    :deep(.dp__input.cell-input) {
    width: 100% !important;
    height: 34px !important;
    padding: 0 12px !important;
    /* border: 1px solid #d7dde5 !important;
    border-radius: 8px !important; */
    background: #fff !important;
    font-size: 14px !important;
    color: #111 !important;
    box-sizing: border-box !important;
    border: none !important;
    box-shadow: none !important;
    }

    /* ✅ DatePicker clear(X)/아이콘 영역 때문에 높이 깨짐 방지 */
    :deep(.dp__clear_icon),
    :deep(.dp__input_icons),
    :deep(.dp__icon) {
    height: 34px !important;
    }
</style>
